import React, { useCallback, useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ActivityIndicator, Platform } from 'react-native';
import { useTranslation } from 'react-i18next';
import Feather from '@expo/vector-icons/Feather';
import { Paprika, PaprikaTypography, PaprikaSpacing, PaprikaRadius } from '../../../styles/theme';
import { RemoteImage } from '../../shared/RemoteImage';
import { MetadataSuggestionPanel } from '../MetadataSuggestionPanel';
import { acceptBlockers, BLOCKING_GAPS } from '../../../utils/recipeMetadataReview';
import type { ReviewRow as ReviewRowData } from '../../../utils/recipeReviewQueue';
import type { MetadataDecisions } from '../../../types/recipeMetadata';

/**
 * One row of the Review screen, whatever the recipe's source. What differs by
 * source is only what Accept does (an import moves to Ready to publish; a
 * library or catalogue recipe is done) and which repairs make sense (Edit
 * would fork a private copy of a catalogue recipe, so it is not offered).
 */
interface Props {
  row: ReviewRowData;
  suggesting: boolean;
  /** A line under the row: what Re-fetch found, a failure. */
  note?: string | null;
  onDecide: (decisions: MetadataDecisions, options?: { timeTyped?: boolean }) => Promise<void>;
  onAccept: () => Promise<void>;
  onRefetch: () => Promise<void>;
  onAddPhoto: () => Promise<void>;
  onOpen: () => void;
  onEdit?: () => void;
  onReject?: () => Promise<void>;
}

export const ReviewRow: React.FC<Props> = ({
  row,
  suggesting,
  note,
  onDecide,
  onAccept,
  onRefetch,
  onAddPhoto,
  onOpen,
  onEdit,
  onReject,
}) => {
  const { t } = useTranslation();
  const [busy, setBusy] = useState<'accept' | 'refetch' | 'photo' | 'reject' | null>(null);

  const run = useCallback(async (kind: NonNullable<typeof busy>, fn: () => Promise<void>) => {
    setBusy(kind);
    try {
      await fn();
    } finally {
      setBusy(null);
    }
  }, []);

  const blockers = acceptBlockers(row);
  const contentBlocked = row.gaps.some((g) => BLOCKING_GAPS.includes(g));
  const settled = row.state === 'done' || row.state === 'ready-to-publish';
  const canAccept = !settled && !contentBlocked && blockers.length === 0;

  const sourceDetail = row.source === 'import' ? row.importBatchTitle : row.source === 'library' ? row.importedVia : undefined;

  return (
    <View style={styles.card} testID={`review-row-${row.id}`}
      // @ts-expect-error RNW forwards dataSet as data-* attributes; the QA harness reads them.
      dataSet={{ state: row.state, source: row.source, scope: row.scope }}
    >
      <View style={styles.body}>
        <TouchableOpacity onPress={onOpen} activeOpacity={0.8} style={styles.thumb}>
          <RemoteImage
            uri={row.image}
            variant="thumb"
            style={styles.thumbImg}
            resizeMode="cover"
            fallback={(
              <View style={styles.thumbEmpty}>
                <Feather name="image" size={20} color={Paprika.textMuted} />
              </View>
            )}
          />
        </TouchableOpacity>

        <View style={styles.main}>
          <View style={styles.titleLine}>
            <Text style={styles.title} numberOfLines={2} onPress={onOpen}>{row.title}</Text>
            <View style={[styles.source, row.source === 'import' && styles.sourceImport]}>
              <Text style={[styles.sourceText, row.source === 'import' && styles.sourceTextImport]} numberOfLines={1}>
                {t(`importReview.sourceLabel.${row.source}`)}
                {sourceDetail ? ` · ${sourceDetail}` : ''}
              </Text>
            </View>
          </View>

          {row.gaps.length > 0 ? (
            <View style={styles.gaps}>
              {row.gaps.map((gap) => (
                <View key={gap} style={[styles.gap, BLOCKING_GAPS.includes(gap) && styles.gapBlocking]} testID={`review-gap-${gap}-${row.id}`}>
                  <Text style={[styles.gapText, BLOCKING_GAPS.includes(gap) && styles.gapTextBlocking]}>
                    {t(`importReview.gaps.${gap}`)}
                  </Text>
                </View>
              ))}
            </View>
          ) : null}

          <MetadataSuggestionPanel recipe={row} suggesting={suggesting} onDecide={onDecide} />

          {note ? <Text style={styles.note} testID={`review-note-${row.id}`}>{note}</Text> : null}
        </View>

        <View style={styles.actions}>
          {!settled ? (
            <TouchableOpacity
              testID={`review-accept-${row.id}`}
              style={[styles.btn, canAccept ? styles.btnPrimary : styles.btnLocked]}
              onPress={() => run('accept', onAccept)}
              disabled={!canAccept || busy !== null}
              activeOpacity={0.8}
            >
              {busy === 'accept' ? (
                <ActivityIndicator size="small" color={Paprika.onPrimary} />
              ) : (
                <Text style={canAccept ? styles.btnPrimaryText : styles.btnLockedText}>
                  {contentBlocked
                    ? t('importReview.blockedAccept')
                    : blockers.length > 0
                      ? t('importReview.toCheck', { count: blockers.length })
                      : t('recipeMetadata.accept')}
                </Text>
              )}
            </TouchableOpacity>
          ) : null}
          {canAccept && row.source === 'import' ? (
            <Text style={styles.hint}>{t('importReview.acceptHint')}</Text>
          ) : null}
          {row.gaps.includes('image') ? (
            <ActionButton testID={`review-photo-${row.id}`} label={t('importReview.addPhoto')} busy={busy === 'photo'} onPress={() => run('photo', onAddPhoto)} />
          ) : null}
          {row.sourceUrl ? (
            <ActionButton testID={`review-refetch-${row.id}`} label={t('importReview.refetch')} busy={busy === 'refetch'} onPress={() => run('refetch', onRefetch)} />
          ) : null}
          {onEdit ? <ActionButton testID={`review-edit-${row.id}`} label={t('importReview.edit')} onPress={onEdit} /> : null}
          {onReject ? (
            <ActionButton
              testID={`review-reject-${row.id}`}
              label={t('importReview.reject')}
              danger
              busy={busy === 'reject'}
              onPress={() => run('reject', onReject)}
            />
          ) : null}
        </View>
      </View>
    </View>
  );
};

const ActionButton: React.FC<{ label: string; onPress: () => void; busy?: boolean; danger?: boolean; testID?: string }> = ({
  label, onPress, busy = false, danger = false, testID,
}) => (
  <TouchableOpacity testID={testID} style={[styles.btn, styles.btnQuiet]} onPress={onPress} disabled={busy} activeOpacity={0.7}>
    {busy ? (
      <ActivityIndicator size="small" color={Paprika.textSecondary} />
    ) : (
      <Text style={[styles.btnQuietText, danger && styles.btnDangerText]}>{label}</Text>
    )}
  </TouchableOpacity>
);

const styles = StyleSheet.create({
  card: {
    backgroundColor: Paprika.surfaceLowest,
    borderRadius: PaprikaRadius.md,
    marginHorizontal: PaprikaSpacing.md,
    marginBottom: PaprikaSpacing.sm,
    padding: PaprikaSpacing.md,
    ...Platform.select({
      ios: {
        shadowColor: 'rgba(82,68,60,1)',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.08,
        shadowRadius: 4,
      },
      android: { elevation: 1 },
      web: { boxShadow: '0 1px 4px rgba(82,68,60,0.08)' },
    }),
  },
  body: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: PaprikaSpacing.md,
  },
  thumb: {
    width: 64,
    height: 64,
    borderRadius: PaprikaRadius.sm,
    overflow: 'hidden',
    backgroundColor: Paprika.surfaceLow,
  },
  thumbImg: {
    width: '100%',
    height: '100%',
  },
  thumbEmpty: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  main: {
    flex: 1,
    minWidth: 240,
    gap: PaprikaSpacing.sm,
  },
  titleLine: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: PaprikaSpacing.sm,
  },
  title: {
    ...PaprikaTypography.titleSmall,
    color: Paprika.textPrimary,
    flexShrink: 1,
  },
  source: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: PaprikaRadius.full,
    backgroundColor: Paprika.surfaceLow,
    maxWidth: 320,
  },
  sourceImport: {
    backgroundColor: Paprika.brandSoft,
  },
  sourceText: {
    ...PaprikaTypography.labelSmall,
    color: Paprika.textSecondary,
  },
  sourceTextImport: {
    color: Paprika.primary,
  },
  gaps: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  gap: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: PaprikaRadius.sm,
    backgroundColor: Paprika.surfaceLow,
  },
  gapBlocking: {
    backgroundColor: Paprika.approvalNoTint,
  },
  gapText: {
    ...PaprikaTypography.labelSmall,
    color: Paprika.textSecondary,
  },
  gapTextBlocking: {
    color: Paprika.approvalNo,
  },
  note: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textSecondary,
  },
  actions: {
    gap: 6,
    minWidth: 150,
    alignItems: 'stretch',
  },
  btn: {
    minHeight: 34,
    paddingHorizontal: 14,
    paddingVertical: 7,
    borderRadius: PaprikaRadius.sm,
    alignItems: 'center',
    justifyContent: 'center',
  },
  btnPrimary: {
    backgroundColor: Paprika.primary,
  },
  btnPrimaryText: {
    ...PaprikaTypography.labelMedium,
    color: Paprika.onPrimary,
  },
  btnLocked: {
    backgroundColor: Paprika.surfaceLow,
  },
  btnLockedText: {
    ...PaprikaTypography.labelMedium,
    color: Paprika.textMuted,
  },
  btnQuiet: {
    backgroundColor: Paprika.background,
  },
  btnQuietText: {
    ...PaprikaTypography.labelMedium,
    color: Paprika.textSecondary,
  },
  btnDangerText: {
    color: Paprika.destructive,
  },
  hint: {
    ...PaprikaTypography.labelSmall,
    color: Paprika.textMuted,
    textAlign: 'center',
  },
});

export default ReviewRow;
